import React, { useRef, useCallback, useEffect } from 'react';

const SPRING = { stiffness: 636, damping: 24, mass: 1 };
const SETTLE_THRESHOLD = 0.001;
const VELOCITY_THRESHOLD = 0.001;

function stepSpring(state, target, dt, { stiffness, damping, mass }) {
    const acceleration = (-stiffness * (state.value - target) - damping * state.velocity) / mass;
    state.velocity += acceleration * dt;
    state.value += state.velocity * dt;
}

function isSpringSettled(state, target) {
    return (
        Math.abs(state.value - target) < SETTLE_THRESHOLD &&
        Math.abs(state.velocity) < VELOCITY_THRESHOLD
    );
}

function TeamMemoji({ member, index, isMobile }) {
    const tooltipRef = useRef(null);
    const hoveredRef = useRef(false);
    const rafRef = useRef(null);
    const lastTimeRef = useRef(null);
    const springY = useRef({ value: 8, velocity: 0 });
    const springOpacity = useRef({ value: 0, velocity: 0 });

    const tick = useCallback((timestamp) => {
        if (lastTimeRef.current == null) {
            lastTimeRef.current = timestamp;
        }
        const dt = Math.min((timestamp - lastTimeRef.current) / 1000, 0.064);
        lastTimeRef.current = timestamp;

        const hovered = hoveredRef.current;
        const targetY = hovered ? 0 : 8;
        const targetOpacity = hovered ? 1 : 0;

        stepSpring(springY.current, targetY, dt, SPRING);
        stepSpring(springOpacity.current, targetOpacity, dt, SPRING);

        const tooltip = tooltipRef.current;

        if (tooltip) {
            tooltip.style.transform = `translateX(-50%) translateY(${springY.current.value}px)`;
            tooltip.style.opacity = String(Math.max(0, Math.min(1, springOpacity.current.value)));
            tooltip.style.visibility =
                springOpacity.current.value < 0.01 && !hovered ? 'hidden' : 'visible';
        }

        const settled =
            isSpringSettled(springY.current, targetY) &&
            isSpringSettled(springOpacity.current, targetOpacity);

        if (!settled) {
            rafRef.current = requestAnimationFrame(tick);
        } else {
            rafRef.current = null;
            lastTimeRef.current = null;
        }
    }, []);

    const startAnimation = useCallback(() => {
        if (rafRef.current != null) {
            cancelAnimationFrame(rafRef.current);
        }
        lastTimeRef.current = null;
        rafRef.current = requestAnimationFrame(tick);
    }, [tick]);

    const onMouseEnter = () => {
        hoveredRef.current = true;
        startAnimation();
    };

    const onMouseLeave = () => {
        hoveredRef.current = false;
        startAnimation();
    };

    useEffect(() => () => {
        if (rafRef.current != null) {
            cancelAnimationFrame(rafRef.current);
        }
    }, []);

    return (
        <div
            className="team-memoji"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                marginTop: '10px',
                marginLeft: index > 0 ? (isMobile ? '5px' : '10px') : 0,
            }}
        >
            <span
                ref={tooltipRef}
                className="team-memoji__tooltip"
                style={{ opacity: 0, visibility: 'hidden' }}
            >
                {member.name}
            </span>
            <img
                src={member.src}
                alt={member.name}
                style={{
                    width: isMobile ? '30px' : '50px',
                    height: isMobile ? '30px' : '50px',
                    borderRadius: isMobile ? '15px' : '25px',
                }}
            />
        </div>
    );
}

export default TeamMemoji;
